import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { guestsApi, reservationsApi } from '../services/api';
import { format } from 'date-fns';

interface GuestDetailDialogProps {
  open: boolean;
  guestId: number | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface GuestDetail {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  loyalty_points: number;
  created_at: string;
}

const GuestDetailDialog: React.FC<GuestDetailDialogProps> = ({
  open,
  guestId,
  onClose,
  onSuccess,
}) => {
  const [guest, setGuest] = useState<GuestDetail | null>(null);
  const [reservations, setReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<any>({});

  useEffect(() => {
    if (open && guestId) {
      fetchGuest();
      fetchReservations();
    }
  }, [open, guestId]);

  const fetchGuest = async () => {
    if (!guestId) return;
    try {
      setLoading(true);
      const response = await guestsApi.getById(guestId);
      setGuest(response.data.data);
      setEditData({
        first_name: response.data.data.first_name,
        last_name: response.data.data.last_name,
        email: response.data.data.email || '',
        phone: response.data.data.phone || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load guest');
    } finally {
      setLoading(false);
    }
  };

  const fetchReservations = async () => {
    if (!guestId) return;
    try {
      const response = await reservationsApi.getAll({ guest_id: guestId });
      setReservations(response.data.data || []);
    } catch (err) {
      console.error('Failed to load reservations:', err);
    }
  };

  const handleUpdate = async () => {
    if (!guestId) return;

    try {
      setProcessing(true);
      setError(null);
      await guestsApi.update(guestId, editData);
      setEditing(false);
      fetchGuest();
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update guest');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'success' | 'warning' | 'error' | 'info' | 'default' } = {
      pending: 'warning',
      confirmed: 'info',
      checked_in: 'success',
      checked_out: 'default',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const totalSpent = reservations.reduce((sum, res) => sum + (res.paid_amount || 0), 0);
  const totalReservations = reservations.length;
  const activeReservations = reservations.filter(r => 
    r.status === 'confirmed' || r.status === 'checked_in'
  ).length;

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!guest) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {guest.first_name} {guest.last_name}
          </Typography>
          {guest.loyalty_points > 0 && (
            <Chip
              label={`${guest.loyalty_points} Points`}
              color="primary"
              size="small"
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Guest Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Guest Information</Typography>
                  {!editing && (
                    <Button size="small" onClick={() => setEditing(true)}>
                      Edit
                    </Button>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">First Name</Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.first_name}
                        onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                        margin="dense"
                        required
                      />
                    ) : (
                      <Typography variant="body1">{guest.first_name}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Last Name</Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.last_name}
                        onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                        margin="dense"
                        required
                      />
                    ) : (
                      <Typography variant="body1">{guest.last_name}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Email</Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        type="email"
                        size="small"
                        value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        margin="dense"
                      />
                    ) : (
                      <Typography variant="body1">{guest.email || 'N/A'}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Phone</Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editData.phone}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        margin="dense"
                      />
                    ) : (
                      <Typography variant="body1">{guest.phone || 'N/A'}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Member Since</Typography>
                    <Typography variant="body1">
                      {format(new Date(guest.created_at), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Statistics */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Total Reservations</Typography>
                    <Typography variant="h4">{totalReservations}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Active Reservations</Typography>
                    <Typography variant="h4" color="primary.main">{activeReservations}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">Total Spent</Typography>
                    <Typography variant="h4" color="success.main">${totalSpent.toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Reservation History */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Booking History</Typography>
                {reservations.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No reservations found for this guest.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Room</TableCell>
                          <TableCell>Check-In</TableCell>
                          <TableCell>Check-Out</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Paid</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reservations.map((reservation) => (
                          <TableRow key={reservation.id}>
                            <TableCell>
                              {reservation.room ? `Room ${reservation.room.number}` : 'TBD'}
                            </TableCell>
                            <TableCell>
                              {format(new Date(reservation.check_in_date), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>
                              {format(new Date(reservation.check_out_date), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={reservation.status.replace('_', ' ')}
                                color={getStatusColor(reservation.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>${reservation.total_amount.toFixed(2)}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color={reservation.paid_amount >= reservation.total_amount ? 'success.main' : 'warning.main'}
                              >
                                ${reservation.paid_amount.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {editing ? (
          <>
            <Button onClick={() => { setEditing(false); fetchGuest(); }} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} variant="contained" disabled={processing}>
              {processing ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GuestDetailDialog;

